/**
 * useExploreSections Hook
 * 
 * Centralized data fetching for Home & Explore screens.
 * Loads all section data (carousels, collections, etc.) in one place.
 */

import { useState, useEffect, useCallback } from 'react';
import { FireBook } from '../models/FirestoreModels';
import { BookService } from '../services/bookService';
import { getBookOfDay } from '../services/bookOfDayService';
import {
  GURU_RECOMMENDATIONS,
  CURATED_COLLECTIONS,
  HEAVY_HITTERS,
  GuruRecommendation,
  CuratedCollection,
} from '../screens/shared/exploreMockData';

export interface BookOfDayData {
  book: FireBook;
  reason: string;
  isPersonalized: boolean;
}

export interface GuruRecommendationWithBooks extends GuruRecommendation {
  books: FireBook[];
  loading: boolean;
  error: string | null;
}

export interface CollectionWithBooks extends CuratedCollection {
  books: FireBook[];
  loading: boolean;
  error: string | null;
}

export interface ExploreSectionsData {
  bookOfDay: BookOfDayData | null;
  guruRecommendations: GuruRecommendationWithBooks[];
  latestSummaries: FireBook[];
  collections: CollectionWithBooks[];
  heavyHitters: FireBook[];
  popularNow: FireBook[];
}

export interface UseExploreSectionsResult {
  data: ExploreSectionsData;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Load books by IDs in batch (parallel, fast)
 * Uses getBooksById which fetches all books in parallel without insights
 */
async function loadBooksByIds(
  bookIds: string[],
  language: string = 'en'
): Promise<FireBook[]> {
  if (bookIds.length === 0) return [];
  
  try {
    const result = await BookService.getBooksById(bookIds, language);
    if (result.success && result.data) {
      return result.data;
    } else {
      console.warn(`⚠️ Failed to load books:`, result.error);
      return [];
    }
  } catch (error) {
    console.warn(`⚠️ Error loading books:`, error);
    return [];
  }
}

/**
 * Main hook for loading all explore sections
 */
export function useExploreSections(
  userId: string | null,
  language: string = 'en'
): UseExploreSectionsResult {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [data, setData] = useState<ExploreSectionsData>({
    bookOfDay: null,
    guruRecommendations: [],
    latestSummaries: [],
    collections: [],
    heavyHitters: [],
    popularNow: [],
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Loading explore sections...');

      // Load book of the day
      const bookOfDayResult = await getBookOfDay(userId, language);
      
      // Load featured/popular books for dynamic sections
      const [newBooksResult, popularBooksResult, featuredBooksResult] = await Promise.all([
        BookService.getNewBooks([], language, 8),
        BookService.getPopularBooks(language, 8),
        BookService.getFeaturedBooks(language, 10),
      ]);

      const latestSummaries = newBooksResult.success && newBooksResult.data 
        ? newBooksResult.data 
        : [];
      
      const popularNow = popularBooksResult.success && popularBooksResult.data 
        ? popularBooksResult.data 
        : [];

      // Initialize guru recommendations with loading state
      const guruRecommendations: GuruRecommendationWithBooks[] = GURU_RECOMMENDATIONS.map(guru => ({
        ...guru,
        books: [],
        loading: true,
        error: null,
      }));

      // Initialize collections with loading state
      const collections: CollectionWithBooks[] = CURATED_COLLECTIONS.map(collection => ({
        ...collection,
        books: [],
        loading: true,
        error: null,
      }));

      // Set initial data with loading states
      setData({
        bookOfDay: bookOfDayResult,
        guruRecommendations,
        latestSummaries,
        collections,
        heavyHitters: [], // Will load below
        popularNow,
      });

      console.log('✅ Initial sections loaded');

      // Load guru recommendation books in background
      Promise.all(
        GURU_RECOMMENDATIONS.map(async (guru, index) => {
          try {
            const books = await loadBooksByIds(guru.bookIds.slice(0, 6), language);
            
            setData(prev => {
              const updated = [...prev.guruRecommendations];
              updated[index] = {
                ...updated[index],
                books,
                loading: false,
                error: null,
              };
              return { ...prev, guruRecommendations: updated };
            });
          } catch (err) {
            console.error(`Error loading guru ${guru.id} books:`, err);
            setData(prev => {
              const updated = [...prev.guruRecommendations];
              updated[index] = {
                ...updated[index],
                loading: false,
                error: 'Failed to load books',
              };
              return { ...prev, guruRecommendations: updated };
            });
          }
        })
      );

      // Load collection books in background
      Promise.all(
        CURATED_COLLECTIONS.map(async (collection, index) => {
          try {
            const books = await loadBooksByIds(collection.bookIds, language);
            
            setData(prev => {
              const updated = [...prev.collections];
              updated[index] = {
                ...updated[index],
                books,
                loading: false,
                error: null,
              };
              return { ...prev, collections: updated };
            });
          } catch (err) {
            console.error(`Error loading collection ${collection.id} books:`, err);
            setData(prev => {
              const updated = [...prev.collections];
              updated[index] = {
                ...updated[index],
                loading: false,
                error: 'Failed to load books',
              };
              return { ...prev, collections: updated };
            });
          }
        })
      );

      // Load heavy hitters
      loadBooksByIds(HEAVY_HITTERS.bookIds, language).then(books => {
        setData(prev => ({ ...prev, heavyHitters: books }));
      });

      setLoading(false);
    } catch (err) {
      console.error('Error loading explore sections:', err);
      setError(err instanceof Error ? err.message : 'Failed to load content');
      setLoading(false);
    }
  }, [userId, language]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    data,
    loading,
    error,
    refetch: loadData,
  };
}

